import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * Page controls for a list.
 *
 * Every list endpoint paginates (CLAUDE.md §5), so every list needs this. It
 * reports the range as well as the page — "Showing 21–40 of 137" answers "have
 * I seen them all", which "Page 2 of 7" does not.
 *
 * It renders nothing at all for a single page. A pagination bar under four rows
 * is noise.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  /** What the rows are, for the count line and the screen-reader label. */
  itemNoun = 'results',
}: {
  /** 1-based. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  itemNoun?: string;
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (pageCount <= 1) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <nav
      aria-label={`${itemNoun} pages`}
      className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row"
    >
      {/*
        `aria-live` so moving between pages is announced. Without it a screen
        reader user presses Next and hears nothing change.
      */}
      <p aria-live="polite" className="text-meta text-ink-500">
        Showing {first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()}{' '}
        {itemNoun}
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => {
            onPageChange(page - 1);
          }}
        >
          <ChevronLeft aria-hidden="true" />
          Previous
        </Button>
        <span className="px-1 text-meta text-ink-500">
          Page {page.toLocaleString()} of {pageCount.toLocaleString()}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pageCount}
          onClick={() => {
            onPageChange(page + 1);
          }}
        >
          Next
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
    </nav>
  );
}
